import useLatest from "@-/com.npmjs.use-latest";
import {
  useLearnCardActionPersistence,
  useLearnCardArrival,
  useNavigateToLearnRouteDestination,
  usePrepareLearnCardDestination,
  useResolveLearnCardDestination,
} from "@-/dev.improve.haniverse.routes.nav.app.learn._lib.card-action";
import { useLearnDeckNavigatorOptions } from "@-/dev.improve.haniverse.routes.nav.app.learn.deck.navigator";
import { useDailyNewCardsSettingsPageNavigator } from "@-/dev.improve.haniverse.routes.nav.app.settings.daily-new-cards.navigator";
import { useCallback, useMemo, type PropsWithChildren } from "react";
import { LearnDeckOperationsContext } from "#learn-deck-operations-context";

function useOperations() {
  const arrive = useLearnCardArrival();
  // The deck owns navigation, so it hands persistence the one route enrollment can offer.
  const dailyNewCardsSettingsPageNavigator = useDailyNewCardsSettingsPageNavigator();
  const controller = useLearnCardActionPersistence({
    openDailyNewCardsSettings: () => dailyNewCardsSettingsPageNavigator.push.mutate(),
  });
  const navigate = useNavigateToLearnRouteDestination();
  const prepare = usePrepareLearnCardDestination();
  const resolve = useResolveLearnCardDestination();
  const navigatorOptionsRef = useLatest(useLearnDeckNavigatorOptions());
  const navigatorOptions = useCallback(
    (...args: Parameters<ReturnType<typeof useLearnDeckNavigatorOptions>>) =>
      navigatorOptionsRef.current(...args),
    [navigatorOptionsRef],
  );
  return useMemo(
    () => ({ arrive, controller, navigate, navigatorOptions, prepare, resolve }),
    [arrive, controller, navigate, navigatorOptions, prepare, resolve],
  );
}

export type LearnDeckOperations = ReturnType<typeof useOperations>;

/** Own reactive query/navigation bindings once, above focus and surface-readiness updates. */
export function LearnDeckOperationsProvider({ children }: PropsWithChildren) {
  const operations = useOperations();
  return <LearnDeckOperationsContext value={operations}>{children}</LearnDeckOperationsContext>;
}
